import { NormalizedTicketTypeVariety } from '../../types/ticketPriceInformation';
import { consumer, core, safeEnumCompare } from '@tock/coretypes';
import dashboardProtos from 'utils/protos';

const { TicketType } = dashboardProtos;

export type OfferingType = consumer.Offering.Type;

export function isTicketTypeReservationVariety(ticketType?: core.ITicketType) {
  if (!ticketType) {
    return false;
  }
  return isReservationVariety(ticketType.variety);
}

export function isReservationVariety(variety?: core.TicketType.Variety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.RESERVATION);
}

export function isTicketTypeDiningVariety(ticketType?: core.ITicketType | null) {
  if (!ticketType) {
    return false;
  }
  return isDiningVariety(ticketType.variety);
}

export function isDiningVariety(variety?: core.TicketType.Variety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return (
    isReservationVariety(variety) ||
    safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.DEPOSIT) ||
    safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.FULL)
  );
}

export function isTicketTypeEventVariety(ticketType?: core.ITicketType) {
  if (!ticketType) {
    return false;
  }
  return isEventVariety(ticketType.variety);
}

export function isEventVariety(variety?: NormalizedTicketTypeVariety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return (
    safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.DEPRECATED_EVENT) ||
    isGAEventVariety(variety) ||
    isSeatedEventVariety(variety)
  );
}

export function isGAEventVariety(variety?: NormalizedTicketTypeVariety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.GA_EVENT);
}

export function isSeatedEventVariety(variety?: NormalizedTicketTypeVariety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.SEATED_EVENT);
}
export function isOrderVariety(variety?: core.TicketType.Variety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return (
    safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.ORDER_DELIVERY) ||
    safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.ORDER_PICKUP)
  );
}

export function isDeliveryVariety(variety?: NormalizedTicketTypeVariety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.ORDER_DELIVERY);
}
interface ThirdPartyDeliveryTicketType extends core.ITicketType {
  variety: 'ORDER_DELIVERY';
  deliveryServiceProvider: Exclude<core.DeliveryServiceProvider, 'INTERNAL'>;
}

export function isThirdPartyDeliveryVariety(
  ticketType?: core.ITicketType | null
): ticketType is ThirdPartyDeliveryTicketType {
  return (
    !!ticketType?.variety &&
    isDeliveryVariety(ticketType.variety) &&
    ticketType.deliveryServiceProvider !== 'INTERNAL'
  );
}

export function getOptionalThirdPartyDeliveryService(
  ticketType?: core.ITicketType | null
): ThirdPartyDeliveryTicketType['deliveryServiceProvider'] | undefined {
  if (isThirdPartyDeliveryVariety(ticketType)) {
    return ticketType.deliveryServiceProvider;
  }
  return undefined;
}

export function isPickupVariety(variety?: NormalizedTicketTypeVariety | OfferingType | null) {
  if (!variety) {
    return false;
  }
  return safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.ORDER_PICKUP);
}

export function isTicketTypeDeliveryVariety(ticketType?: core.ITicketType) {
  if (!ticketType) {
    return false;
  }
  return safeEnumCompare(ticketType.variety, TicketType.Variety, TicketType.Variety.ORDER_DELIVERY);
}

export function isTicketTypePickupVariety(ticketType?: core.ITicketType) {
  if (!ticketType) {
    return false;
  }
  return safeEnumCompare(ticketType.variety, TicketType.Variety, TicketType.Variety.ORDER_PICKUP);
}

export function isTablesideVariety(variety?: NormalizedTicketTypeVariety | OfferingType) {
  if (!variety) {
    return false;
  }

  return safeEnumCompare(variety, TicketType.Variety, TicketType.Variety.TABLESIDE);
}

export function isTicketTypeTablesideVariety(ticketType?: core.ITicketType) {
  if (!ticketType) {
    return false;
  }
  return isTablesideVariety(ticketType.variety || undefined);
}

export function isTicketTypeTogoVariety(ticketType?: core.ITicketType) {
  return isTogoVariety(ticketType?.variety);
}

export function isTogoVariety(variety?: NormalizedTicketTypeVariety | null) {
  if (!variety) {
    return false;
  }
  return isDeliveryVariety(variety) || isPickupVariety(variety);
}
